// Shared Stripe Embedded Checkout controller.
//
// Handles the full same-page payment lifecycle for a single (event, viewer):
//   1. Pay Now → POST /api/payments/checkout → embedded iframe mounts.
//   2. Stripe onComplete → poll GET /api/payments/session/{id} (self-heals
//      against Stripe if the webhook is delayed / lost).
//   3. Soft timeout → recovery card with a "Retry Status" button that
//      ONLY re-polls (never mints a new session, never re-charges).
//
// Consumed by both the Club Event and Tournament payment surfaces.
// Presentation is intentionally minimal — the parent owns the label
// (amount / method / paid state chip). This component owns the inline
// iframe and the retry lifecycle.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Warning } from '@phosphor-icons/react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { paymentsAPI } from '../../lib/paymentsApi';

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

/**
 * @param {object}   props
 * @param {'tournament'|'round_robin'|'club_event'} props.eventKind
 * @param {string}   props.eventId
 * @param {object=}  props.viewer                Signed-in user (null when anon).
 * @param {boolean}  props.viewerCanPay          Registration guard resolved by parent.
 * @param {string=}  props.notEligibleReason     Optional inline hint text when !viewerCanPay.
 * @param {string=}  props.ctaLabel              Button label (default "Pay Now").
 * @param {()=>void} props.onPaid                Called after local status flips to 'paid'.
 * @param {string=}  props.testIdPrefix          Prefix for data-testids (default 'stripe-pay').
 * @param {number=}  props.autoStartToken        When this value changes to a
 *                                               truthy number, the component
 *                                               auto-invokes startCheckout()
 *                                               (used by the shared post-
 *                                               registration payment prompt).
 */
export default function StripeEmbeddedPay({
  eventKind,
  eventId,
  viewer,
  viewerCanPay,
  notEligibleReason,
  ctaLabel = 'Pay Now',
  onPaid,
  testIdPrefix = 'stripe-pay',
  autoStartToken = 0,
}) {
  const [busy, setBusy] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // 'idle' | 'polling' | 'awaiting_confirmation'.
  const [confirmPhase, setConfirmPhase] = useState('idle');
  const [retrying, setRetrying] = useState(false);
  const pollTimer = useRef(null);
  const pollStopped = useRef(false);

  useEffect(() => () => {
    pollStopped.current = true;
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  const pollUntilPaid = useCallback((sid, { softTimeoutMs = 30000 } = {}) => {
    if (!sid) return;
    pollStopped.current = false;
    const start = Date.now();
    const tick = async () => {
      if (pollStopped.current) return;
      try {
        const { data } = await paymentsAPI.getSession(sid);
        if (data.status === 'paid') {
          pollStopped.current = true;
          setClientSecret(null);
          setSessionId(null);
          setConfirming(false);
          setPolling(false);
          setConfirmPhase('idle');
          toast.success('Payment received. Thanks!');
          onPaid?.();
          return;
        }
      } catch { /* transient — keep polling */ }
      if (Date.now() - start > softTimeoutMs) {
        pollStopped.current = true;
        setClientSecret(null);
        setPolling(false);
        setConfirming(false);
        setConfirmPhase('awaiting_confirmation');
        return;
      }
      pollTimer.current = setTimeout(tick, 1500);
    };
    setPolling(true);
    tick();
  }, [onPaid]);

  const startCheckout = async () => {
    if (busy) return;
    if (!stripePromise) {
      toast.error('Payments are not configured. Try again shortly.');
      return;
    }
    if (!viewer) {
      toast.error('Sign in to pay for this event.');
      return;
    }
    if (!viewerCanPay) {
      toast.error(notEligibleReason || 'Register for this event before paying.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await paymentsAPI.createCheckout({ eventKind, eventId });
      setClientSecret(data.client_secret);
      setSessionId(data.session_id);
      setConfirmPhase('idle');
    } catch (e) {
      const d = e.response?.data?.detail;
      const code = typeof d === 'object' ? d?.code : null;
      const msg = typeof d === 'string' ? d : d?.message || 'Could not start payment.';
      if (code === 'registration_required') {
        toast.error(notEligibleReason || 'Register for this event before paying.');
      } else if (code === 'already_paid') {
        toast.success("You\u2019re already marked paid.");
        onPaid?.();
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };

  // Auto-start on token bump — triggered by the shared post-registration
  // payment prompt when the user taps "Pay {amount} Now". We only fire
  // when the iframe is NOT already mounted and the viewer is eligible;
  // otherwise the click is silently absorbed (prevents double sessions).
  const lastAutoStartToken = useRef(0);
  useEffect(() => {
    if (!autoStartToken) return;
    if (autoStartToken === lastAutoStartToken.current) return;
    lastAutoStartToken.current = autoStartToken;
    if (clientSecret || confirmPhase !== 'idle') return;
    if (!viewer || !viewerCanPay || !stripePromise) return;
    startCheckout();
  }, [autoStartToken]);

  const handleEmbeddedComplete = useCallback(() => {
    setConfirming(true);
    setConfirmPhase('polling');
    pollUntilPaid(sessionId);
  }, [sessionId, pollUntilPaid]);

  const retryConfirmation = useCallback(async () => {
    if (!sessionId || retrying) return;
    setRetrying(true);
    try {
      const { data } = await paymentsAPI.getSession(sessionId);
      if (data.status === 'paid') {
        setSessionId(null);
        setConfirmPhase('idle');
        toast.success('Payment received. Thanks!');
        onPaid?.();
      } else {
        setConfirmPhase('polling');
        pollUntilPaid(sessionId, { softTimeoutMs: 15000 });
      }
    } catch {
      toast.error("Couldn\u2019t reach Stripe just now. Try again in a moment.");
    } finally { setRetrying(false); }
  }, [sessionId, retrying, onPaid, pollUntilPaid]);

  const cancelEmbedded = () => {
    setClientSecret(null);
    setSessionId(null);
  };

  return (
    <div className="space-y-3">
      {/* CTA button — hidden once the iframe is mounted. */}
      {!clientSecret && confirmPhase === 'idle' && (
        <button
          type="button"
          onClick={startCheckout}
          disabled={busy || !viewer || !viewerCanPay}
          data-testid={`${testIdPrefix}-btn`}
          className="inline-flex items-center gap-1.5 rounded-full min-h-[44px] px-5 py-2 text-sm font-semibold bg-[#013523] text-white hover:bg-[#024029] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <CreditCard size={14} weight="bold" />
          {busy ? 'Opening…' : ctaLabel}
        </button>
      )}

      {/* Ineligible viewer hint (registered flag is false / signed-out). */}
      {!clientSecret && confirmPhase === 'idle' && viewer && !viewerCanPay && notEligibleReason && (
        <div className="flex items-start gap-1.5 text-[12px] text-[#7c5b1a]"
             data-testid={`${testIdPrefix}-need-registration`}>
          <Warning size={13} weight="fill" className="mt-0.5 shrink-0" />
          <span>{notEligibleReason}</span>
        </div>
      )}
      {!clientSecret && confirmPhase === 'idle' && !viewer && (
        <div className="text-[12px] text-[#6a766e]"
             data-testid={`${testIdPrefix}-signin-hint`}>
          Sign in to pay for this event.
        </div>
      )}

      {/* Embedded checkout iframe — only rendered while we have a
          client_secret. Unmounts on complete/cancel/timeout. */}
      {clientSecret && stripePromise && (
        <div className="rounded-2xl border border-[#e3dcd1] bg-[#faf7f0] p-2"
             data-testid={`${testIdPrefix}-embedded`}>
          {confirming && (
            <div className="text-center py-2 text-[12px] text-[#013523] font-medium"
                 data-testid={`${testIdPrefix}-confirming`}>
              Confirming payment…
            </div>
          )}
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret, onComplete: handleEmbeddedComplete }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
          {!polling && (
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={cancelEmbedded}
                data-testid={`${testIdPrefix}-cancel-embedded`}
                className="text-[12px] text-[#6a766e] hover:text-[#013523] underline"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Recovery card — Stripe onComplete fired but our poll couldn't
          confirm within the soft timeout. Never mints a new session. */}
      {confirmPhase === 'awaiting_confirmation' && sessionId && (
        <div
          data-testid={`${testIdPrefix}-awaiting`}
          className="rounded-2xl border border-[#c9a93a]/40 bg-[#fff9e6]/70 px-4 py-3.5 space-y-2"
        >
          <div className="flex items-start gap-2">
            <Warning size={16} weight="fill" className="text-[#7c5b1a] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-[#3a2c05]">
                Payment submitted — we&rsquo;re confirming it with Stripe.
              </div>
              <div className="text-[12px] text-[#5a4b1e] mt-0.5">
                Your card was charged successfully. This can take a few extra seconds. Your registration stays intact either way.
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={retryConfirmation}
              disabled={retrying}
              data-testid={`${testIdPrefix}-retry-status`}
              className="inline-flex items-center gap-1.5 rounded-full min-h-[40px] px-4 py-1.5 text-[13px] font-semibold bg-[#013523] text-white hover:bg-[#024029] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {retrying ? 'Checking…' : 'Retry Status'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
